// Styles
import './CaptchaPasswordReset.css'

import HCaptcha from '@hcaptcha/react-hcaptcha'
// External Imports
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { CAPTCHA_ERRORS, MAX_RETRY_COUNT } from 'src/dataLayer/localData/captcha.js'
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream'
import { reportCaptchaError } from 'src/dataLayer/remoteData/rollbar'
import { useCaptchaConfigContext } from 'src/viewLayer/components/contexts/CaptchaConfigContext'
// Internal Imports
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext'

import useHandleCaptchaPasswordResetRequest from './useHandleCaptchaPasswordResetRequest'

// Component Definition
function CaptchaPasswordReset({
  setCaptchaToken,
  shouldExecuteCaptcha,
  shouldTrackTokenRequested = true,
}) {
  const { locale } = useRouteParamsContext()
  const { setCaptchaConfig } = useCaptchaConfigContext()
  const { captchaConfig, makeCaptchaPasswordResetRequest } = useHandleCaptchaPasswordResetRequest()
  const captchaReference = useRef(null)
  const [retryCount, setRetryCount] = useState(0)
  const isConfigAvailable = Boolean(captchaConfig?.provider && captchaConfig?.siteKey)

  useEffect(() => {
    if (captchaConfig?.siteKey) {
      setCaptchaConfig({ siteKey: captchaConfig.siteKey })
    }
  }, [captchaConfig, setCaptchaConfig])

  const executeCaptcha = useCallback(() => {
    if (captchaReference.current) {
      captchaReference.current.resetCaptcha()
      captchaReference.current.execute('captcha-password-reset')
    }
  }, [captchaReference.current])

  useEffect(() => {
    if (shouldExecuteCaptcha) {
      executeCaptcha()
    }
  }, [executeCaptcha, shouldExecuteCaptcha])

  const onVerifyCaptcha = useCallback(
    (token) => {
      if (token) {
        setCaptchaToken(token)
        trackAuthenticationResetPassword({ stage: 'captcha-success' })
      }
    },
    [setCaptchaToken],
  )

  const onErrorCaptcha = useCallback(
    (error) => {
      if (CAPTCHA_ERRORS.has(error) && retryCount < MAX_RETRY_COUNT) {
        setRetryCount((previousRetryCount) => previousRetryCount + 1)
        executeCaptcha()
        trackAuthenticationResetPassword({ stage: 'captcha-failure' })
      } else {
        reportCaptchaError('Error while retry verifying captcha:', { code: error })
      }
    },
    [retryCount],
  )

  const onExpireCaptcha = useCallback(() => {
    setCaptchaToken('')
    executeCaptcha()
  }, [setCaptchaToken])

  useEffect(() => {
    if (!isConfigAvailable) {
      ;(async () => {
        if (shouldTrackTokenRequested) {
          trackAuthenticationResetPassword({ stage: 'token-requested' })
        }
        await makeCaptchaPasswordResetRequest()
      })()
    }
  }, [isConfigAvailable, shouldTrackTokenRequested])

  if (!captchaConfig) {
    return null
  }

  return (
    <div className="hcaptcha-container">
      <HCaptcha
        id="captcha-password-reset"
        languageOverride={locale === 'en_GB' ? 'en' : locale}
        onChalExpired={onExpireCaptcha}
        onError={onErrorCaptcha}
        onExpire={onExpireCaptcha}
        onVerify={onVerifyCaptcha}
        ref={captchaReference}
        sitekey={captchaConfig.siteKey}
        size="invisible"
      />
    </div>
  )
}

export { CaptchaPasswordReset }
